import axios from 'axios'
import { notify } from '@/utils/notification'

/**
 * Default error messages by HTTP status code
 */
const DEFAULT_ERROR_MESSAGES = {
  400: 'Invalid request. Please check your input.',
  401: 'Authentication required. Please log in.',
  403: 'Access denied. You don\'t have permission.',
  404: 'Resource not found.',
  409: 'Conflict. Resource already exists.',
  422: 'Validation failed. Please check your data.',
  429: 'Too many requests. Please try again later.',
  500: 'Server error. Please try again.',
  503: 'Service unavailable. Please try again later.',
  default: 'An error occurred. Please try again.',
  network: 'Network error. Please check your connection.'
}

/**
 * Default success messages by HTTP method
 */
const DEFAULT_SUCCESS_MESSAGES = {
  GET: 'Data retrieved successfully',
  POST: 'Created successfully',
  PUT: 'Updated successfully',
  PATCH: 'Updated successfully',
  DELETE: 'Deleted successfully'
}

/**
 * ApiClient - Singleton HTTP client for API requests
 *
 * Features:
 * - Automatic request/response interceptors
 * - Per-request notification configuration
 * - Centralized error handling
 * - Support for all HTTP methods (GET, POST, PUT, DELETE, PATCH)
 * - Environment-based configuration
 *
 * @example
 * const apiClient = ApiClient.getInstance()
 *
 * // With notifications
 * await apiClient.post('/bookings', data, {
 *   notification: {
 *     showNotification: true,
 *     successMessage: 'Booking created successfully!',
 *     errorMessage: 'Failed to create booking'
 *   }
 * })
 *
 * // Silent request (no notifications)
 * const data = await apiClient.get('/availability', {
 *   notification: { showNotification: false }
 * })
 */
class ApiClient {
  /**
   * Singleton instance
   * @private
   */
  static instance = null

  /**
   * Axios instance
   * @private
   */
  axiosInstance = null

  /**
   * Base URL for API requests
   * @private
   */
  baseURL = null

  /**
   * Request timeout in milliseconds
   * @private
   */
  timeout = 30000

  /**
   * Private constructor for singleton pattern
   * @param {string} baseURL - Base API URL
   * @param {Object} config - Optional axios configuration
   * @param {number} [config.timeout=30000] - Request timeout in milliseconds
   * @param {Object} [config.headers={}] - Default headers
   * @private
   */
  constructor(baseURL, config = {}) {
    if (ApiClient.instance) {
      return ApiClient.instance
    }

    // Validate baseURL
    if (!baseURL || baseURL === 'undefined') {
      const errorMsg = `
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
⚠️  API BASE URL NOT CONFIGURED ⚠️

The environment variable NEXT_PUBLIC_API_BASE_URL is not set.
This will cause API requests to fail.

📝 Required Environment Variable:
   NEXT_PUBLIC_API_BASE_URL=https://api.legacyassetleaders.com/api

🔧 How to fix:
   - For local development: Add to .env.local file
   - For production: Set in your deployment platform
     (Vercel/Netlify/etc.) environment variables

Current value: ${baseURL}
━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
      `
      console.error(errorMsg)
      throw new Error('NEXT_PUBLIC_API_BASE_URL environment variable is required')
    }

    this.baseURL = baseURL
    this.timeout = config.timeout || parseInt(process.env.NEXT_PUBLIC_API_TIMEOUT) || 30000

    // Log configuration in debug mode
    if (process.env.NEXT_PUBLIC_API_DEBUG === 'true') {
      console.log('[API Client] Initialized with baseURL:', this.baseURL)
      console.log('[API Client] Timeout:', this.timeout, 'ms')
    }

    // Create axios instance
    this.axiosInstance = axios.create({
      baseURL: this.baseURL,
      timeout: this.timeout,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        ...config.headers
      }
    })

    // Setup interceptors
    this._setupInterceptors()

    ApiClient.instance = this
  }

  /**
   * Get singleton instance of ApiClient
   * @param {string} [baseURL=process.env.NEXT_PUBLIC_API_BASE_URL] - Base API URL
   * @param {Object} [config={}] - Optional configuration
   * @returns {ApiClient} Singleton instance
   *
   * @example
   * const apiClient = ApiClient.getInstance()
   * const apiClient = ApiClient.getInstance('https://api.example.com')
   */
  static getInstance(baseURL = process.env.NEXT_PUBLIC_API_BASE_URL, config = {}) {
    if (!ApiClient.instance) {
      new ApiClient(baseURL, config)
    }
    return ApiClient.instance
  }

  /**
   * Setup request and response interceptors
   * @private
   */
  _setupInterceptors() {
    // Request interceptor
    this.axiosInstance.interceptors.request.use(
      (config) => {
        // Log request in debug mode
        if (process.env.NEXT_PUBLIC_API_DEBUG === 'true') {
          console.log(`[API] ${config.method.toUpperCase()} ${config.url}`, {
            params: config.params,
            data: config.data
          })
        }

        // Future: Add authentication token
        // const token = getAuthToken()
        // if (token) {
        //   config.headers['Authorization'] = `Bearer ${token}`
        // }

        return config
      },
      (error) => {
        if (process.env.NEXT_PUBLIC_API_DEBUG === 'true') {
          console.error('[API] Request error:', error)
        }
        return Promise.reject(error)
      }
    )

    // Response interceptor
    this.axiosInstance.interceptors.response.use(
      (response) => {
        // Log response in debug mode
        if (process.env.NEXT_PUBLIC_API_DEBUG === 'true') {
          console.log(`[API] Response from ${response.config.url}`, {
            status: response.status,
            data: response.data
          })
        }

        return response
      },
      (error) => {
        // Log error in debug mode
        if (process.env.NEXT_PUBLIC_API_DEBUG === 'true') {
          console.error(`[API] Error from ${error.config?.url}`, {
            status: error.response?.status,
            message: error.message,
            data: error.response?.data
          })
        }

        return Promise.reject(error)
      }
    )
  }

  /**
   * Handle error responses
   * @param {Error} error - Axios error object
   * @param {Object} notificationConfig - Notification configuration
   * @param {string} method - HTTP method for default messages
   * @returns {never} Throws enhanced error
   * @private
   */
  _handleError(error, notificationConfig = {}, method = '') {
    let errorMessage = ''
    let statusCode = null

    if (error.response) {
      // Server responded with error status
      statusCode = error.response.status
      errorMessage = error.response.data?.detail ||
                     error.response.data?.message ||
                     error.response.data?.error ||
                     DEFAULT_ERROR_MESSAGES[statusCode] ||
                     DEFAULT_ERROR_MESSAGES.default
    } else if (error.request) {
      // Request made but no response
      errorMessage = DEFAULT_ERROR_MESSAGES.network
    } else {
      // Request setup error
      errorMessage = error.message || DEFAULT_ERROR_MESSAGES.default
    }

    // Show notification if enabled and not suppressed
    if (notificationConfig.showNotification && !notificationConfig.suppressError) {
      const displayMessage = notificationConfig.errorMessage || errorMessage
      notify.error(displayMessage)
    }

    // Execute error callback
    if (notificationConfig.onError) {
      notificationConfig.onError(error)
    }

    // Log error in development
    if (process.env.NODE_ENV === 'development') {
      console.error('[API] Error:', {
        url: error.config?.url,
        method: error.config?.method,
        status: statusCode,
        message: errorMessage,
        data: error.response?.data
      })
    }

    // Throw enhanced error
    const enhancedError = new Error(errorMessage)
    enhancedError.originalError = error
    enhancedError.statusCode = statusCode
    enhancedError.response = error.response
    throw enhancedError
  }

  /**
   * Handle successful responses
   * @param {Object} response - Axios response object
   * @param {Object} notificationConfig - Notification configuration
   * @param {string} method - HTTP method for default messages
   * @returns {any} Response data
   * @private
   */
  _handleSuccess(response, notificationConfig = {}, method = '') {
    // Show success notification if enabled
    if (notificationConfig.showNotification) {
      const successMessage = notificationConfig.successMessage ||
                            DEFAULT_SUCCESS_MESSAGES[method] ||
                            DEFAULT_SUCCESS_MESSAGES.POST
      notify.success(successMessage)
    }

    // Execute success callback
    if (notificationConfig.onSuccess) {
      notificationConfig.onSuccess(response)
    }

    return response.data
  }

  /**
   * Perform GET request
   * @param {string} url - Endpoint URL (relative to baseURL)
   * @param {Object} [options={}] - Request options
   * @param {Object} [options.headers] - Request-specific headers
   * @param {Object} [options.params] - URL query parameters
   * @param {number} [options.timeout] - Request timeout override
   * @param {Object} [options.notification] - Notification configuration
   * @param {boolean} [options.notification.showNotification=false] - Enable/disable notifications
   * @param {string} [options.notification.successMessage] - Custom success message
   * @param {string} [options.notification.errorMessage] - Custom error message
   * @param {Function} [options.notification.onSuccess] - Success callback
   * @param {Function} [options.notification.onError] - Error callback
   * @param {boolean} [options.suppressError=false] - Skip error notification
   * @returns {Promise<any>} Response data
   * @throws {Error} Request error with notification
   *
   * @example
   * // Silent request
   * const data = await apiClient.get('/users', {
   *   notification: { showNotification: false }
   * })
   *
   * // With notification
   * const users = await apiClient.get('/users', {
   *   params: { page: 1, limit: 10 },
   *   notification: {
   *     showNotification: true,
   *     successMessage: 'Users loaded successfully'
   *   }
   * })
   */
  async get(url, options = {}) {
    try {
      const response = await this.axiosInstance.get(url, {
        headers: options.headers,
        params: options.params,
        timeout: options.timeout
      })

      return this._handleSuccess(response, options.notification || {}, 'GET')
    } catch (error) {
      this._handleError(error, options.notification || {}, 'GET')
    }
  }

  /**
   * Perform POST request
   * @param {string} url - Endpoint URL
   * @param {Object} data - Request payload
   * @param {Object} [options={}] - Request options
   * @param {Object} [options.headers] - Request-specific headers
   * @param {Object} [options.params] - URL query parameters
   * @param {number} [options.timeout] - Request timeout override
   * @param {Object} [options.notification] - Notification configuration
   * @param {boolean} [options.notification.showNotification=false] - Enable/disable notifications
   * @param {string} [options.notification.successMessage] - Custom success message
   * @param {string} [options.notification.errorMessage] - Custom error message
   * @param {Function} [options.notification.onSuccess] - Success callback
   * @param {Function} [options.notification.onError] - Error callback
   * @param {boolean} [options.suppressError=false] - Skip error notification
   * @returns {Promise<any>} Response data
   *
   * @example
   * await apiClient.post('/bookings', formData, {
   *   notification: {
   *     showNotification: true,
   *     successMessage: 'Booking created successfully!',
   *     errorMessage: 'Failed to create booking'
   *   }
   * })
   */
  async post(url, data, options = {}) {
    try {
      const response = await this.axiosInstance.post(url, data, {
        headers: options.headers,
        params: options.params,
        timeout: options.timeout
      })

      return this._handleSuccess(response, options.notification || {}, 'POST')
    } catch (error) {
      this._handleError(error, options.notification || {}, 'POST')
    }
  }

  /**
   * Perform PUT request
   * @param {string} url - Endpoint URL
   * @param {Object} data - Request payload
   * @param {Object} [options={}] - Request options
   * @param {Object} [options.headers] - Request-specific headers
   * @param {Object} [options.params] - URL query parameters
   * @param {number} [options.timeout] - Request timeout override
   * @param {Object} [options.notification] - Notification configuration
   * @returns {Promise<any>} Response data
   *
   * @example
   * await apiClient.put('/users/123', userData, {
   *   notification: {
   *     showNotification: true,
   *     successMessage: 'User updated successfully'
   *   }
   * })
   */
  async put(url, data, options = {}) {
    try {
      const response = await this.axiosInstance.put(url, data, {
        headers: options.headers,
        params: options.params,
        timeout: options.timeout
      })

      return this._handleSuccess(response, options.notification || {}, 'PUT')
    } catch (error) {
      this._handleError(error, options.notification || {}, 'PUT')
    }
  }

  /**
   * Perform PATCH request
   * @param {string} url - Endpoint URL
   * @param {Object} data - Request payload
   * @param {Object} [options={}] - Request options
   * @param {Object} [options.headers] - Request-specific headers
   * @param {Object} [options.params] - URL query parameters
   * @param {number} [options.timeout] - Request timeout override
   * @param {Object} [options.notification] - Notification configuration
   * @returns {Promise<any>} Response data
   *
   * @example
   * await apiClient.patch('/users/123', { status: 'active' }, {
   *   notification: {
   *     showNotification: true,
   *     successMessage: 'Status updated'
   *   }
   * })
   */
  async patch(url, data, options = {}) {
    try {
      const response = await this.axiosInstance.patch(url, data, {
        headers: options.headers,
        params: options.params,
        timeout: options.timeout
      })

      return this._handleSuccess(response, options.notification || {}, 'PATCH')
    } catch (error) {
      this._handleError(error, options.notification || {}, 'PATCH')
    }
  }

  /**
   * Perform DELETE request
   * @param {string} url - Endpoint URL
   * @param {Object} [options={}] - Request options
   * @param {Object} [options.headers] - Request-specific headers
   * @param {Object} [options.params] - URL query parameters
   * @param {number} [options.timeout] - Request timeout override
   * @param {Object} [options.notification] - Notification configuration
   * @returns {Promise<any>} Response data
   *
   * @example
   * await apiClient.delete('/users/123', {
   *   notification: {
   *     showNotification: true,
   *     successMessage: 'User deleted successfully',
   *     onSuccess: () => refreshUserList()
   *   }
   * })
   */
  async delete(url, options = {}) {
    try {
      const response = await this.axiosInstance.delete(url, {
        headers: options.headers,
        params: options.params,
        timeout: options.timeout
      })

      return this._handleSuccess(response, options.notification || {}, 'DELETE')
    } catch (error) {
      this._handleError(error, options.notification || {}, 'DELETE')
    }
  }
}

export default ApiClient
